import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TableContainer,
  Typography,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ZoomIn, Edit } from '@mui/icons-material';
import LogoSpinner from 'src/components/LogoSpinner';
import {
  DynamicTable,
  TableSearch
} from 'src/components/DynamicTable/DynamicTable';
import { ReferralCodeResponse, ReferralCodeStatus } from './referral.interface';
import { useReferral } from 'src/hooks/useReferral';

const TABLE_TEXT_STYLE = {
  p: '2%',
  height: '100%',
  display: 'flex',
  alignItems: 'center'
};

interface ReferralContentProps {
  loading: boolean;
  page: number;
  rowsPerPage: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  onRefresh?: () => void;
  referrals: ReferralCodeResponse[];
}

export const ReferralContent: FC<ReferralContentProps> = ({
                                                            loading,
                                                            page,
                                                            rowsPerPage,
                                                            setPage,
                                                            setRowsPerPage,
                                                            referrals,
                                                            onRefresh
                                                          }) => {
  const [filtered, setFiltered] = useState<ReferralCodeResponse[]>(
    referrals || []
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState<ReferralCodeResponse | null>(null);
  const [editable, setEditable] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [statusValue, setStatusValue] = useState<ReferralCodeStatus>(null);

  const {
    loading: apiLoading,
    getReferralCodeById,
    updateReferralCodeStatus,
    getReferralCodes
  } = useReferral();


  const updateReferralStatus = async (newStatus: ReferralCodeStatus) => {
    if (!selected) return;
    const ok = await updateReferralCodeStatus(selected.id, { status: newStatus });
    if (ok) {
      onRefresh?.();
      const full = await getReferralCodeById(selected.id);
      setSelected(full || { ...selected, status: newStatus });
      setIsEditingStatus(false);

    }
  };

  // Set Defaults
 const setDefaults = ()=> {
    setEditable(false);
    setStatusValue(null);
 }

 const statusChip = (status: ReferralCodeStatus) => (
    <Chip
      title={status}
      label={status}
      color={status === 'ACTIVE' ? 'success' : 'default'}
      size="small"
    />
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleView = async (row: ReferralCodeResponse) => {
    setOpenDialog(true);
    const full = await getReferralCodeById(row.id);
    setSelected(full || row);
  };

  const headers = useMemo(
    () => [
      { headerName: 'Code', field: 'code', minWidth: 120 },
      { headerName: 'Email / Referrer', field: 'referrerEmail', minWidth: 160 },
      {
        headerName: 'Commission',
        field: 'commissionRate',
        minWidth: 120,
        renderCell: ({ row }) => (
          <Box sx={TABLE_TEXT_STYLE}>
            {typeof row.commissionRate === 'number'
              ? `${row.commissionRate}%`
              : row.commissionRate}
          </Box>
        )
      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 120,
        renderCell: ({ row }) => (
          <Box sx={TABLE_TEXT_STYLE}>{statusChip(row.status)}</Box>
        )
      },
      { headerName: 'Usage Count', field: 'totalUsages', minWidth: 100 },
      {
        headerName: 'Created',
        field: 'createdAt',
        minWidth: 140,
        renderCell: ({ row }) => (
          <Box sx={TABLE_TEXT_STYLE}>{formatDate(row.createdAt)}</Box>
        )
      },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filterable: false,
        width: 140,
        renderCell: ({ row }) => (
          <Button
            size="small"
            startIcon={<ZoomIn />}
            onClick={() => handleView(row)}
          >
            View
          </Button>
        )
      }
    ],
    []
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          py: '5%'
        }}
      >
        <LogoSpinner />
      </Box>
    );
  }

  return (
    <>
      <Card>
        <TableContainer>
          <DynamicTable
            showPagination={true}
            rowsPerPage={rowsPerPage}
            page={page}
            totalPage={referrals?.length ?? 0}
            searchElement={
              <TableSearch
                placeholder="Search Referral Codes"
                searchData={[]}
                setSearchData={setFiltered}
              />
            }
            rows={(referrals || []).map((d) => ({
              // Spread full ReferralCodeResponse to keep native properties available
              ...d,
              // Populate fields expected by headers using ReferralCodeResponse properties
              code: d.code,
              referrerEmail:
                (d as any).referrerEmail ?? d.referrerEmail ?? 'N/A',
              commissionRate: d.commissionRate,
              status: d.status,
              usageCount: (d as any).usageCount ?? 0,
              createdAt: d.createdAt,
              rowHeight: 55
            }))}
            headers={headers}
            handleChangePage={(event, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(event) => setRowsPerPage(event)}
          />
        </TableContainer>
      </Card>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {!apiLoading && (
            <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
              View Referral ({selected?.code})
            </span>
          )}
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {apiLoading && (
            <Box
              sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <LogoSpinner />
            </Box>
          )}
          {!apiLoading && selected && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                py: 1
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Code
                </Typography>
                <Typography>{selected.code}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Discount
                </Typography>
                <Typography>{selected.discountValue ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Referrer
                </Typography>
                <Typography>{selected.referrerEmail ?? 'N/A'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Commission
                </Typography>
                <Typography>
                  {typeof selected.commissionRate === 'number'
                    ? `${selected.commissionRate}%`
                    : selected.commissionRate}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Max per user
                </Typography>
                <Typography>
                  {selected.maxUsagePerUser ?? 'Unlimited'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Max total
                </Typography>
                <Typography>{selected.maxUsageTotal ?? 'Unlimited'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography>{formatDate(selected.createdAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Updated
                </Typography>
                <Typography>{formatDate(selected.updatedAt)}</Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                {!editable ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selected.status.toUpperCase()}
                    <IconButton
                      size="small"
                      aria-label="edit status"
                      onClick={() => setEditable(true)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <FormControl size="small" sx={{width: 'auto'}}>
                    <Select
                      value={statusValue || selected.status}
                      onChange={(e) => {
                        const val = e.target.value as ReferralCodeStatus;
                        setStatusValue(val);

                      }}
                      disabled={apiLoading}
                    >
                      <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                      <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editable && (
            <Button onClick={() => {
              updateReferralStatus(statusValue).then(() => {
                setEditable(false);
                setOpenDialog(false);
                setDefaults();
              });
            }}>Update</Button>
          )}
          <Button onClick={() => {
            setOpenDialog(false);
            setDefaults();

          }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReferralContent;
